import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Project } from "../generated/content";
import { useLanguage } from "../context/LanguageContext";
import { projectMeta } from "../lib/projectMeta";
import "../style/HeroSlider.css";

const INTERVAL_MS = 10000;

function prefersReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

interface HeroSliderProps {
  projects: Project[];
  onFirstImageLoad?: () => void;
}

export default function HeroSlider({ projects, onFirstImageLoad }: HeroSliderProps) {
  const { lang, t } = useLanguage();
  const [current, setCurrent] = useState(0);
  // Content that moves on its own must be pausable (WCAG 2.2.2). Visitors who
  // asked the system for reduced motion start paused.
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const count = projects.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + count) % count);
  }, [count]);

  useEffect(() => {
    if (!playing || count < 2) return;
    timer.current = setInterval(() => setCurrent((prev) => (prev + 1) % count), INTERVAL_MS);
    return () => clearInterval(timer.current);
  }, [playing, count, current]);

  if (count === 0) return null;

  const active = projects[current];
  const meta = projectMeta(active, lang);

  return (
    <section className="hero" data-hero aria-roledescription="carousel" aria-label="Projeler">
      {projects.map((project, index) => {
        // Only the visible slide and the one queued behind it are fetched.
        // Loading all five up front downloaded four images nobody looked at.
        const isNext = index === (current + 1) % count;
        const shouldLoad = index === current || isNext;

        return (
          <Link
            key={project.slug}
            to={`/projects/${project.slug}`}
            className={`hero__slide${index === current ? " is-active" : ""}`}
            tabIndex={index === current ? 0 : -1}
            aria-hidden={index !== current}
          >
            {shouldLoad && (
              <img
                src={project.cover.src}
                srcSet={project.cover.srcSet}
                sizes="100vw"
                alt={t(project.title)}
                onLoad={index === 0 ? onFirstImageLoad : undefined}
                onError={index === 0 ? onFirstImageLoad : undefined}
              />
            )}
          </Link>
        );
      })}

      <div className="hero__caption">
        <h1>{t(active.title)}</h1>
        {meta && <p className="meta-line hero__meta">{meta}</p>}
      </div>

      <div className="hero__controls">
        {count > 1 && (
          <button
            type="button"
            className="hero__toggle"
            onClick={() => setPlaying((value) => !value)}
            aria-label={playing ? "Otomatik geçişi durdur" : "Otomatik geçişi başlat"}
          >
            <span aria-hidden="true">{playing ? "❙❙" : "▶"}</span>
          </button>
        )}

        <div className="hero__dots" role="tablist" aria-label="Projeler">
          {projects.map((project, index) => (
            <button
              key={project.slug}
              type="button"
              role="tab"
              aria-current={index === current}
              aria-label={t(project.title)}
              onClick={() => goTo(index)}
            >
              <span />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
